/**
 * Login com Google no app nativo.
 *
 * POR QUE EXISTE: no webview o botão do `@react-oauth/google` simplesmente não
 * renderiza — a origem é `https://localhost`, que não está nas origens
 * autorizadas do client. E mesmo que estivesse, o Google **bloqueia OAuth em
 * webview embutido** (`disallowed_useragent`). Só o SDK nativo funciona.
 *
 * COMO SE ENCAIXA NO BACKEND: no Android o token é emitido para o **web client
 * ID** (configurado como `webClientId`), então o `aud` do idToken é o mesmo que
 * o do site. O `/auth/google` já aceita esse audience — nada muda no servidor.
 *
 * Na web isto é no-op: `disponivel()` dá false e o Login mantém o `GoogleLogin`.
 */

import { isNative, getPlatform, getPlugin } from './native';

const WEB_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const IOS_CLIENT_ID = process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID || '';

let iniciado = false;

function plugin() {
  return getPlugin('SocialLogin');
}

export function disponivel() {
  return isNative() && !!plugin() && !!WEB_CLIENT_ID;
}

async function garantirInicializado(p) {
  if (iniciado) return true;
  try {
    await p.initialize({
      google: {
        // Android e Web usam o web client ID; iOS precisa do próprio, mais o
        // web como serverClientId para o idToken sair com o mesmo audience.
        webClientId: WEB_CLIENT_ID,
        iOSClientId: IOS_CLIENT_ID || undefined,
        iOSServerClientId: WEB_CLIENT_ID,
      },
    });
    iniciado = true;
    return true;
  } catch {
    return false;
  }
}

/**
 * Abre o fluxo nativo e devolve o idToken para trocar por sessão no backend.
 *
 * @returns {Promise<{ ok: boolean, idToken?: string, motivo?: string }>}
 *   motivo: 'indisponivel' | 'cancelado' | 'sem-token' | 'erro'
 *
 * Cancelar não é erro: a usuária fechou o seletor de conta de propósito.
 */
export async function entrar() {
  const p = plugin();
  if (!p || !WEB_CLIENT_ID) return { ok: false, motivo: 'indisponivel' };
  if (!(await garantirInicializado(p))) return { ok: false, motivo: 'erro' };

  try {
    // SEM `scopes`: o plugin exige alterar a MainActivity para aceitar escopos
    // customizados, e nao precisamos de nenhum — `profile` e `email` ja vem no
    // fluxo padrao de OIDC, que e tudo que o /auth/google usa (sub e email).
    const r = await p.login({ provider: 'google', options: {} });
    // O formato varia entre versões do plugin; procuramos o idToken onde ele
    // costuma vir, em vez de assumir um caminho só.
    const idToken =
      r?.result?.idToken ||
      r?.result?.authentication?.idToken ||
      r?.idToken ||
      null;

    if (!idToken) return { ok: false, motivo: 'sem-token' };
    return { ok: true, idToken };
  } catch (e) {
    const msg = ((e && (e.message || e.code)) || '').toString().toLowerCase();
    if (msg.includes('cancel') || msg.includes('12501')) return { ok: false, motivo: 'cancelado' };
    return { ok: false, motivo: 'erro' };
  }
}

/** Encerra a sessão do Google no aparelho (usado no logout). */
export async function sair() {
  const p = plugin();
  if (!p || !iniciado) return;
  try {
    await p.logout({ provider: 'google' });
  } catch { /* melhor esforço: o logout do app não depende disto */ }
}

/** Só para diagnóstico em log. */
export function plataforma() {
  return getPlatform();
}
